import React, { useState, useEffect } from "react";
import { Container } from "react-bootstrap";
import { useDebounce } from '../../hooks/usedeBounce';
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import styles from "./Admin.module.css";
import Header from "../../components/Header";
import NavBar from "../../components/NavBar";
import Footer from "../../components/Footer";
import DataTable from "../Admin/Datatable";
import AddFuncionarioModal from "../Admin/AddFuncionarioModal";
import EditFuncionarioModal from "../Admin/EditFuncionarioModal";
import AddOrganicaModal from "../Admin/AddOrganicaModal";
import EditOrganicaModal from "../Admin/EditOrganicaModal";
import AddEstablecimientoModal from "../Admin/AddEstablecimientoModal";
import EditEstablecimientoModal from "../Admin/EditEstablecimientoModal";
import AddReparticionGdeModal from "../Admin/AddReparticionGdeModal";
import EditReparticionGdeModal from "../Admin/EditReparticionGdeModal";
import AddSectorGdeModal from "../Admin/AddSectorGdeModal";
import EditSectorGdeModal from "../Admin/EditSectorGdeModal";
import EditInactiveModal from "../Admin/EditInactiveModal";
import DeleteModal from "../Admin/DeleteModal";
import ReactivateInactiveModal from "../Admin/ReactivateInactiveModal"
import ConfigsCrudTables from "./configsCrudTables";
import { Funcionario, Organica, Establecimiento, ReparticionGde, SectorGde, Inactivo } from "../../types/interfaces";
import { useLoading } from "../../components/LoadingContext";

type EntityMap = {
  funcionario: Funcionario;
  organica: Organica;
  establecimiento: Establecimiento;
  reparticionGde: ReparticionGde;
  sectorGde: SectorGde;
  inactivo: Inactivo;
};

const Admin = () => {
  const [key, setKey] = useState<keyof EntityMap>("funcionario");
  const { setLoading } = useLoading();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [sortConfig, setSortConfig] = useState<{ key: string | null; direction: string | null }>({ key: 'id', direction: 'ASC' });
  const [totalItems, setTotalItems] = useState(0);
  const debouncedFilter = useDebounce(globalFilterValue, 1500);
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [organicas, setOrganica] = useState<Organica[]>([]);
  const [establecimientos, setEstablecimientos] = useState<Establecimiento[]>([]);
  const [reparticionesGde, setReparticionesGde] = useState<ReparticionGde[]>([]);
  const [sectoresGde, setSectoresGde] = useState<SectorGde[]>([]);
  const [inactivos, setInactivos] = useState<Inactivo[]>([]);
  const [modalAction, setModalAction] = useState<"add" | "edit" | "delete" | "reactivated" | null>(null);

useEffect(() => {
  const fetchData = async () => {
    setLoading(true);
    try {
      const config = ConfigsCrudTables[key];
      if (!config?.fetchUrl) return;

      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: itemsPerPage.toString(),
        filter: debouncedFilter,
        sortBy: sortConfig.key || "",
        sortOrder: sortConfig.direction || "",
      });

      const response = await fetch(`${config.fetchUrl}?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });
      if (!response.ok) throw new Error("Error al cargar los datos");

      const result = await response.json();

      switch (key) {
        case "funcionario":
          setFuncionarios(result.data);
          break;
        case "organica":
          setOrganica(result.data);
          break;
        case "establecimiento":
          setEstablecimientos(result.data);
          break;
        case "reparticionGde":
          setReparticionesGde(result.data);
          break;
        case "sectorGde":
          setSectoresGde(result.data);
          break;
        case "inactivo":
          setInactivos(result.data);
          break;
      }

      setTotalItems(result.total || 0);
    } catch (error) {
      console.error("Error en fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchData();
}, [key, currentPage, itemsPerPage, debouncedFilter, sortConfig, setLoading]);

  const modals: { [key: string]: { [action: string]: React.FC<any> } } = {
    funcionario: {
      add: AddFuncionarioModal,
      edit: EditFuncionarioModal,
      delete: DeleteModal,
    },
    organica: {
      add: AddOrganicaModal,
      edit: EditOrganicaModal,
      delete: DeleteModal,
    },
    establecimiento: {
      add: AddEstablecimientoModal,
      edit: EditEstablecimientoModal,
      delete: DeleteModal,
    },
    reparticionGde: {
      add: AddReparticionGdeModal,
      edit: EditReparticionGdeModal,
      delete: DeleteModal,
    },
    sectorGde: {
      add: AddSectorGdeModal,
      edit: EditSectorGdeModal,
      delete: DeleteModal,
    },
    inactivo: {
      edit: EditInactiveModal,
      reactivated: ReactivateInactiveModal,
    },
  };

  const config = ConfigsCrudTables[key];
  const columns = config?.columns || [];
  const fetchUrl = config?.fetchUrl || "";

  const ModalComponent = modalAction ? modals[key]?.[modalAction] : undefined;

  const handleAddClick = () => {
    setModalAction("add");
  };
  
  const handleEditClick = () => {
    setModalAction("edit");
  };

  const handleDeleteClick = () => {
    setModalAction("delete");
  }

  const handleReactiveClick = () => {
    setModalAction("reactivated");
  }

  const currentData = (() => {
    switch (key) {
      case "funcionario":
        return funcionarios;
      case "organica":
        return organicas;
      case "establecimiento":
        return establecimientos;
      case "reparticionGde":
        return reparticionesGde;
      case "sectorGde":
        return sectoresGde;
      case "inactivo":
        return inactivos;
      default:
        return []; 
    }
  })();

  return (
    <>
      <Header />
      <NavBar />
      <Container className="full-height-container">
        <Tabs
          activeKey={key}
          onSelect={(k) => {
            if (k !== null) {
              setKey(k as keyof EntityMap);
              setModalAction(null);
              setCurrentPage(1);
              setItemsPerPage(10);
              setGlobalFilterValue("");
              setSortConfig({ key: 'id', direction: 'ASC' });
            }
          }}
          className={styles.tabs}
          fill
        >
          {["funcionario", "organica", "establecimiento", "reparticionGde", "sectorGde", "inactivo"].map((tabKey) => (
            <Tab eventKey={tabKey} title={ConfigsCrudTables[tabKey].label} key={tabKey}>
              <DataTable
                data={currentData as EntityMap[typeof key][]}
                columns={columns}
                fetchUrl={fetchUrl}
                ModalComponent={ModalComponent}
                modalAction={modalAction}
                onAddClick={handleAddClick}
                onEditClick={handleEditClick}
                onDeleteClick={handleDeleteClick}
                onReactiveClick={handleReactiveClick}
                keyActive={key}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                totalItems={totalItems}
                sortConfig={sortConfig}
                globalFilterValue={globalFilterValue}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(value) => {
                  setItemsPerPage(value);
                  setCurrentPage(1);
                }}
                onGlobalFilterChange={(value) => {
                  setGlobalFilterValue(value);
                  setCurrentPage(1);
                }}
                onSortChange={(key, direction) => {
                  setSortConfig({ key, direction });
                }}
              />
            </Tab>
          ))}
        </Tabs>
      </Container>
      <Footer />
    </>
  );
};

export default Admin;
